/* ======================================================================
   Inventory Gate (IS vs DK) + Shared-Delivery Exception + Pattern-safe
   Rules:
     - visitorCountry === IS => IS stock only
     - exception: visitorCountry === IS && B2B && shared-delivery product => DK stock only
     - visitorCountry !== IS => DK stock only

   Pattern products:
     - Any element inside [data-check-pattern="true"] is NEVER disabled/relabeled by this script
     - Cart drawer items with data-check-pattern="true" are never clamped / error-texted by this script
   ====================================================================== */
(function () {
  /* ---------- JSON readers ------------------------------------------ */
  function readJSONById(id) {
    const el = document.getElementById(id);
    if (!el) return null;
    try { return JSON.parse(el.textContent || '{}'); } catch (e) { return null; }
  }

  /* ---------- Config ------------------------------------------------- */
  const cfg = readJSONById('inventory-gate-config') || {};
  const PHP_ENDPOINT      = cfg.phpEndpoint || '';
  const ICELAND_COUNTRY   = String(cfg.icelandCountry || 'IS').toUpperCase();
  const ICELAND_WAREHOUSE = String(cfg.icelandWarehouse || 'IS').toUpperCase();
  const DK_WAREHOUSE      = String(cfg.dkWarehouse || 'DK').toUpperCase();
  const CACHE_TTL         = Number(cfg.cacheTtl || 30000);
  const ERROR_TXT         = cfg.errorTxt || 'Ikke på lager i din region';
  const STRICT_ICELAND    = cfg.strictIceland !== false; // default true

  if (!PHP_ENDPOINT) {
    console.log('[INFO] inventory-gate: missing phpEndpoint. Ensure #inventory-gate-config exists.');
    return;
  }

  /* ---------- Customer context -------------------------------------- */
  const customerCtx = (() => {
    const data = readJSONById('customer-context') || {};
    const ctx = {
      loggedIn: !!data.loggedIn,
      tags: Array.isArray(data.tags) ? data.tags.map(t => String(t).toLowerCase()) : []
    };
    console.log('[INFO] customer ctx', ctx);
    return ctx;
  })();

  function isB2B() {
    // Adjust to match your exact tag scheme if needed
    const match = customerCtx.loggedIn && customerCtx.tags.some(t => t.includes('b2b'));
    console.log('[INFO] isB2B =', match);
    return match;
  }

  /* ---------- Country detection ------------------------------------- */
  function detectCountry() {
    const cookie = document.cookie.split('; ').find(c => c.startsWith('_shopify_country='));
    if (cookie) return cookie.split('=')[1].toUpperCase();
    if (window.Shopify?.country) return String(window.Shopify.country).toUpperCase();
    if (window.Shopify?.locale) return String(window.Shopify.locale).split('-').pop().toUpperCase();
    return DK_WAREHOUSE;
  }

  let visitorCountry = detectCountry();
  console.log('[INFO] visitorCountry =', visitorCountry);

  /* ---------- Shared-delivery detection ----------------------------- */
  // Liquid map expected: <script id="shared-delivery-variants" type="application/json">{"<variantId>":true,...}</script>
  let __SHARED_VARIANTS = null;

  function readSharedVariantsMap() {
    if (__SHARED_VARIANTS) return __SHARED_VARIANTS;

    __SHARED_VARIANTS = {};
    const el = document.getElementById('shared-delivery-variants');
    if (!el) {
      console.log('[INFO] shared-delivery-variants not found (not a PDP or snippet missing)');
      return __SHARED_VARIANTS;
    }

    try {
      __SHARED_VARIANTS = JSON.parse(el.textContent || '{}') || {};
      console.log('[INFO] shared-delivery map loaded', __SHARED_VARIANTS);
    } catch (e) {
      console.log('[INFO] shared-delivery map parse error', e);
    }

    return __SHARED_VARIANTS;
  }

  function isSharedDeliveryVariant(variantId) {
    // PDP map wins
    const map = readSharedVariantsMap();
    const fromMap = !!map[String(variantId)];
    if (fromMap) {
      console.log('[INFO] isSharedDeliveryVariant', variantId, '=> true (PDP map)');
      return true;
    }
    // Cart cache fallback (only works if properties[_shared_delivery] is set)
    const fromCart = !!sharedCache.get(Number(variantId));
    console.log('[INFO] isSharedDeliveryVariant', variantId, '=>', fromCart, '(cart cache)');
    return fromCart;
  }

  /* ---------- Pattern and kit guards ---------------------------------------- */
  function isPatternContext(el) {
    return !!el?.closest?.('[data-check-pattern="true"]');
  }

  function isPatternCartItem(cartItemEl) {
    return String(cartItemEl?.dataset?.checkPattern || '').toLowerCase() === 'true';
  }

  function isPatternPdp() {
    const wrap = document.querySelector('.product-form__quantity__add__buttons[data-check-pattern]') ||
                 document.querySelector('[data-check-pattern][class*="product-form"]');
    return wrap?.dataset?.checkPattern === 'true';
  }

  function isKitB2B() {
    const form = document.querySelector('product-form.product-form[data-product-type="kit-b2b"]');
    const match = !!form;
    console.log('[INFO] isKitB2B =', match);
    return match;
  }

  /* ---------- Cart fallback via line item property ------------------ */
  // requires: <input name="properties[_shared_delivery]" value="true"> on shared-delivery product add-to-cart
  const sharedCache = new Map(); // variantId -> boolean
  async function seedSharedFromCart() {
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' }});
      if (!res.ok) return;
      const cart = await res.json();
      (cart.items || []).forEach(it => {
        const vid = Number(it.variant_id);
        if (!vid) return;
        const props = it.properties || {};
        const flag = String(props._shared_delivery || '').toLowerCase();
        if (flag === 'true' || flag === '1' || flag === 'yes') {
          sharedCache.set(vid, true);
        }
      });
      console.log('[INFO] sharedCache seeded from cart', Object.fromEntries(sharedCache));
    } catch (e) {}
  }

  /* ---------- State -------------------------------------------------- */
  const stockCache = new Map(); // variantId -> { IS: n, DK: n, ts: ms }

  /* ---------- STOCK FETCH ------------------------------------------- */
  async function fetchStock (variantIds = []) {
    const idsToFetch = variantIds.filter(id => {
      const rec = stockCache.get(id);
      const stale = !rec || Date.now() - rec.ts > CACHE_TTL;
      console.log(`[INFO] cache ${stale ? 'MISS' : 'HIT'} → ${id}`);
      return stale;
    });
    if (!idsToFetch.length) return;

    console.log('[INFO] fetching', idsToFetch);
    const res  = await fetch(PHP_ENDPOINT, {
      method : 'POST',
      headers: {'Content-Type':'application/json'},
      body   : JSON.stringify({ variantIds: idsToFetch })
    });
    if (!res.ok) { console.warn('[INFO] fetch failed'); return; }
    const data = await res.json();
    Object.entries(data).forEach(([id, payload]) =>
      stockCache.set(+id, {...payload, ts: Date.now()})
    );
    console.log('[INFO] payload', data);
  }

  /* ---------- RULES -------------------------------------------------- */
  function availableQtyFor(variantId) {
    const rec = stockCache.get(Number(variantId));
    if (!rec) {
      console.log('[INFO] qty unknown (no cache yet) for', variantId);
      return null;
    }

    const isISCustomer = visitorCountry === ICELAND_COUNTRY;
    const isQty = Math.max(0, Number(rec[ICELAND_WAREHOUSE] || 0));
    const dkQty = Math.max(0, Number(rec[DK_WAREHOUSE] || 0));

    const shared = isSharedDeliveryVariant(variantId);
    const b2b = isB2B();

    if (isISCustomer) {
      if (b2b && shared) {
        console.log('[INFO] rule: IS + B2B + shared-delivery => DK only | DK=', dkQty);
        return dkQty;
      }
      console.log('[INFO] rule: IS customer => IS only | IS=', isQty);
      return isQty;
    }

    console.log('[INFO] rule: non-IS => DK only | DK=', dkQty);
    return dkQty;
  }

  function allowedToBuy(variantId, qtyNeeded = 1) {
    const qty = availableQtyFor(variantId);

    if (qty === null) {
      const strictBlock = STRICT_ICELAND && visitorCountry === ICELAND_COUNTRY;
      console.log('[INFO] allowedToBuy pending stock', { variantId, strictBlock });
      return !strictBlock;
    }

    const ok = qty >= qtyNeeded;
    console.log('[INFO] allowedToBuy', { variantId, qtyNeeded, qty, ok });
    return ok;
  }

  /* ---------- Drawer helpers ---------------------------------------- */
  function drawerItemVariantId(el) {
    const v = Number(el.dataset.variantId);
    if (v) return v;

    const link = el.querySelector('a[href*="variant="]')?.href || '';
    const id = +(new URL(link, location.origin).searchParams.get('variant'));
    return id || null;
  }

  function collectDrawerVariantIds() {
    return [...document.querySelectorAll('#CartDrawer .cart-item[data-variant-id], .drawer__cart-items-wrapper .cart-item[data-variant-id]')]
      .map(drawerItemVariantId)
      .filter(Boolean);
  }

  function updateDrawerLineLimits(cartItemEl) {
    const variantId = drawerItemVariantId(cartItemEl);
    if (!variantId) return;

    if (isPatternCartItem(cartItemEl)) {
      // Never clamp or show region errors for pattern lines
      console.log('[INFO] drawer line skipped (pattern)', cartItemEl.dataset.variantId);
      const err = cartItemEl.querySelector('.cart-item__error-text');
      if (err) err.textContent = '';
      cartItemEl.classList.remove('error');
      cartItemEl.querySelector('input.quantity__input')?.removeAttribute('max');
      cartItemEl.querySelector('button.quantity__button[name="plus"]')?.classList.remove('disabled');
      return;
    }

    const inp  = cartItemEl.querySelector('input.quantity__input');
    const plus = cartItemEl.querySelector('button.quantity__button[name="plus"]');
    const err  = cartItemEl.querySelector('.cart-item__error-text');

    if (!inp || !plus) return;

    const avail = availableQtyFor(variantId);

    if (avail === null) {
      inp.removeAttribute('max');
      plus.classList.remove('disabled');
      if (err) err.textContent = '';
      cartItemEl.classList.remove('error');
      console.log('[INFO] drawer line pending stock', variantId);
      return;
    }

    const max = Math.max(0, Number(avail) || 0);
    inp.setAttribute('max', String(max));

    const min = Number(inp.getAttribute('min')) || 0;
    let cur   = Number(inp.value);
    if (Number.isNaN(cur)) cur = min;

    const clamped = Math.min(Math.max(cur, min), max);
    if (String(inp.value) !== String(clamped)) inp.value = String(clamped);

    const atMax = clamped >= max;
    const okPlus = max > 0 && !atMax;

    plus.classList.toggle('disabled', !okPlus);

    if (!okPlus) {
      if (err) err.textContent = ERROR_TXT;
      cartItemEl.classList.add('error');
    } else {
      if (err) err.textContent = '';
      cartItemEl.classList.remove('error');
    }

    console.log('[INFO] drawer limits set', { variantId, min, max, cur: clamped, okPlus });
  }

  function refreshDrawerLimits() {
    const items = document.querySelectorAll('#CartDrawer .cart-item[data-variant-id], .drawer__cart-items-wrapper .cart-item[data-variant-id]');
    items.forEach(updateDrawerLineLimits);
  }

  /* ---------- UI UPDATERS ------------------------------------------- */
  function getCurrentVariantId() {
    // Prefer visible form to avoid grabbing variant from another product form
    const forms = [...document.querySelectorAll('form[action*="/cart/add"]')];
    const form = forms.find(f => f.offsetParent !== null) || forms[0];
    const inp = form?.querySelector('[name="id"]');
    const id = inp ? Number(inp.value) : null;
    console.log('[INFO] current variant id =', id);
    return id || null;
  }

  function updateCustomerFacingQty(variantId) {
    const qty = availableQtyFor(variantId);
    if (qty === null) return;

    const customSpan = document.querySelector('.custom-inventory .stock-number');
    if (customSpan) customSpan.textContent = String(qty);

    // You asked to update this too:
    const advSpan = document.querySelector('.product .inventory__title .advantage__title span');
    if (advSpan) advSpan.textContent = String(qty);

    console.log('[INFO] customer-facing qty updated =>', qty);
  }

  function setBtnDisabled(btn, disabled, labelDisabled = 'Ikke på lager') {
    if (!btn) return;
    const isInput = btn.tagName === 'INPUT';
    const setLabel = (text) => {
      if (!isInput) btn.textContent = text;
      btn.querySelector?.('.button__text') && (btn.querySelector('.button__text').textContent = text);
      if (isInput) btn.value = text;
    };
    if (disabled) {
      btn.setAttribute('disabled', 'disabled');
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('inactive');
      setLabel(labelDisabled);
    } else {
      btn.removeAttribute('disabled');
      btn.setAttribute('aria-disabled', 'false');
      btn.classList.remove('inactive');
      setLabel('Tilføj til kurv');
    }
  }

  function updateATCButtons(variantId) {
    // Never touch ATC in pattern context
    if (isPatternPdp()) {
      console.log('[INFO] updateATCButtons skipped (pattern PDP)');
      return;
    }
    if (isKitB2B()) {
      console.log('[INFO] updateATCButtons skipped (kit-b2b)');
      return;
    }

    const ok = allowedToBuy(variantId, 1);

    const buttons = [
      ...document.querySelectorAll('#AddToCart'),
      ...document.querySelectorAll('.product-form__submit')
    ].filter(btn => !isPatternContext(btn));

    buttons.forEach(btn => setBtnDisabled(btn, !ok, 'Ikke på lager'));

    console.log('[INFO] ATC updated =>', ok ? 'ENABLED' : 'DISABLED', '| filtered buttons:', buttons.length);
    updateCustomerFacingQty(variantId);
  }

  function updateVariantSwatches() {
    // swatches are not ATC, but if they live inside pattern wrapper, leave them alone too
    document.querySelectorAll('label.color-swatch[data-variant-id]').forEach(label => {
      if (isPatternContext(label)) return;
      const id = Number(label.dataset.variantId);
      if (!id) return;
      const ok = allowedToBuy(id, 1);
      label.classList.toggle('swatch-sold-out', !ok);
      label.querySelector('.no-stock-badge')?.style.setProperty('display', ok ? 'none' : 'block');
    });
  }

  function collectBundleVariantIds() {
    return [...document.querySelectorAll('.slide-in__values-container .yarn-color[data-id]')]
      .map(el => Number(el.dataset.id))
      .filter(Boolean);
  }

  function updateBundleUI() {
    document.querySelectorAll('.slide-in__values-container .yarn-color[data-id]').forEach(el => {
      if (isPatternContext(el)) return;
      const id = Number(el.dataset.id);
      if (!id) return;
      const ok = allowedToBuy(id, 1);
      el.classList.toggle('yarn-sold-out', !ok);
      el.querySelector('input.yarn-input')?.toggleAttribute('disabled', !ok);
      el.querySelector('.sold-out-badge')?.style.setProperty('display', ok ? 'none' : 'block');
    });
  }

  function cartVariantId(cartItemEl) {
    const link = cartItemEl.querySelector('a[href*="variant="]')?.href || '';
    const id = +(new URL(link, location.origin).searchParams.get('variant'));
    return id || null;
  }

  function collectCartVariantIds() {
    return [...document.querySelectorAll('.drawer__cart-items-wrapper .cart-item')]
      .map(cartVariantId)
      .filter(Boolean);
  }

  function refreshCartControls() {
    document.querySelectorAll('.drawer__cart-items-wrapper .cart-item').forEach(el => {
      if (isPatternCartItem(el)) return;

      const id = cartVariantId(el);
      const qtyInp = el.querySelector('.quantity__input');
      const plus = el.querySelector('.quantity__button[name="plus"]');
      const errBox = el.querySelector('.cart-item__error-text');
      if (!id || !qtyInp || !plus) return;

      const desired = Number(qtyInp.value) + 1;
      const ok = allowedToBuy(id, desired);
      plus.classList.toggle('disabled', !ok);

      if (!ok) {
        if (errBox) errBox.textContent = ERROR_TXT;
        el.classList.add('error');
      } else {
        if (errBox) errBox.textContent = '';
        el.classList.remove('error');
      }
    });
  }

  /* ---------- PDP Quantity (skip pattern) --------------------------- */
  function updatePdpQuantityControls(variantId) {
    if (isPatternPdp()) {
      console.log('[INFO] PDP qty controls skipped (pattern PDP)');
      return;
    }
    if (isKitB2B()) {
      console.log('[INFO] PDP qty controls skipped (kit-b2b)');
      return;
    }

    const inp =
      document.querySelector('.product-parameters__quantity .quantity__input') ||
      document.querySelector('.product .quantity__input');

    if (!inp) return;

    const plus =
      document.querySelector('.product-parameters__quantity .quantity__button-plus') ||
      document.querySelector('.product .quantity__button-plus') ||
      document.querySelector('.product .quantity__button[name="plus"]');

    const minus =
      document.querySelector('.product-parameters__quantity .quantity__button-minus') ||
      document.querySelector('.product .quantity__button-minus') ||
      document.querySelector('.product .quantity__button[name="minus"]');

    const avail = availableQtyFor(variantId);

    if (avail === null) {
      inp.removeAttribute('max');
      plus?.classList.remove('disabled');
      return;
    }

    const max = Math.max(0, Number(avail) || 0);
    const min = Number(inp.getAttribute('min')) || 1;

    inp.setAttribute('max', String(max));

    let cur = Number(inp.value) || min;
    if (max === 0) cur = min;
    else cur = Math.min(Math.max(cur, min), max);
    if (String(inp.value) !== String(cur)) inp.value = String(cur);

    const atMax = max > 0 && cur >= max;
    const atMin = cur <= min;

    plus?.classList.toggle('disabled', max === 0 || atMax);
    minus?.classList.toggle('disabled', atMin);

    console.log('[INFO] PDP qty set', { min, max, cur, avail });
  }

  function bindPdpQuantityClamping() {
    if (isPatternPdp()) return;

    const wrap =
      document.querySelector('.product-parameters__quantity') ||
      document.querySelector('.product');

    if (!wrap) return;

    wrap.addEventListener('input', (e) => {
      const inp = e.target.closest('.quantity__input');
      if (!inp) return;

      const vId = getCurrentVariantId();
      if (!vId) return;

      const min = Number(inp.getAttribute('min')) || 1;
      const maxAttr = inp.getAttribute('max');
      const max = maxAttr != null ? Number(maxAttr) : Infinity;

      let val = Number(inp.value) || min;
      val = Math.min(Math.max(val, min), max);

      if (String(inp.value) !== String(val)) inp.value = String(val);
      updatePdpQuantityControls(vId);
    });

    wrap.addEventListener('click', (e) => {
      const btn = e.target.closest('.quantity__button, .quantity__button-plus, .quantity__button-minus');
      if (!btn) return;

      const vId = getCurrentVariantId();
      if (!vId) return;

      requestAnimationFrame(() => updatePdpQuantityControls(vId));
    });
  }

  /* ---------- Variant change hook ----------------------------------- */
  function ensureVariantHiddenInput() {
    const forms = [...document.querySelectorAll('form[action*="/cart/add"]')];
    const form = forms.find(f => f.offsetParent !== null) || forms[0];
    if (!form) return null;
    let hid = form.querySelector('input[name="id"]');
    if (!hid) {
      hid = document.createElement('input');
      hid.type = 'hidden';
      hid.name = 'id';
      form.appendChild(hid);
    }
    return hid;
  }

  function wireVariantRadios() {
    ensureVariantHiddenInput();

    document.addEventListener('change', async e => {
      const inp = e.target.closest('input[type="radio"][data-variant-radio]');
      if (!inp) return;

      if (isPatternContext(inp)) {
        console.log('[INFO] variant radio change ignored (pattern context)');
        return;
      }

      const vId = Number(inp.dataset.variantId);
      if (!vId) return;

      const hid = ensureVariantHiddenInput();
      if (hid) hid.value = String(vId);

      await fetchStock([vId]);
      updateVariantSwatches();
      updateATCButtons(vId);
      updateBundleUI();
      updatePdpQuantityControls(vId);

      document.body.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant: { id: vId }}}));
    });
  }

  /* ---------- INIT -------------------------------------------------- */
  async function init() {
    console.log('[INFO] inventory-gate init');

    // drawer first
    const drawerIds = collectDrawerVariantIds();
    if (drawerIds.length) {
      await fetchStock(drawerIds);
      refreshDrawerLimits();
    }

    await seedSharedFromCart();
    wireVariantRadios();

    // PDP
    if (window.meta?.product?.variants?.length) {
      const ids = window.meta.product.variants.map(v => Number(v.id)).filter(Boolean);
      await fetchStock(ids);
      updateVariantSwatches();

      const initial = getCurrentVariantId();
      if (initial) {
        updateATCButtons(initial);
        updatePdpQuantityControls(initial);
        bindPdpQuantityClamping();
      }
    }

    // Bundle
    const bundleIds = collectBundleVariantIds();
    if (bundleIds.length) {
      await fetchStock(bundleIds);
      updateBundleUI();
    }

    // Drawer open refresh
    document.addEventListener('cart:drawerOpened', async () => {
      await seedSharedFromCart?.();
      const ids = collectDrawerVariantIds();
      console.log('[INFO] drawer opened ids', ids);
      if (ids.length) {
        await fetchStock(ids);
        refreshDrawerLimits();
      }
    });

    // Variant changed
    document.body.addEventListener('variant:changed', async e => {
      const vId = e?.detail?.variant?.id || getCurrentVariantId();
      if (!vId) return;

      await fetchStock([vId]);
      updateVariantSwatches();
      updateATCButtons(vId);
      updateBundleUI();
      updatePdpQuantityControls(vId);
    });

    // Drawer +/- click guard
    document.addEventListener('click', async (evt) => {
      const btn = evt.target.closest('#CartDrawer button.quantity__button, .drawer__cart-items-wrapper button.quantity__button');
      if (!btn) return;

      const item = btn.closest('.cart-item[data-variant-id]');
      if (!item) return;

      if (isPatternCartItem(item)) return;

      const variantId = drawerItemVariantId(item);
      if (!variantId) return;

      await fetchStock([variantId]);
      updateDrawerLineLimits(item);

      if (btn.name === 'plus' && btn.classList.contains('disabled')) {
        evt.preventDefault();
        evt.stopPropagation();
        console.log('[INFO] drawer plus blocked', variantId);
      }
    });

    // Drawer input typing clamp
    document.addEventListener('input', (evt) => {
      const inp = evt.target.closest('#CartDrawer input.quantity__input, .drawer__cart-items-wrapper input.quantity__input');
      if (!inp) return;
      const item = inp.closest('.cart-item[data-variant-id]');
      if (!item) return;
      updateDrawerLineLimits(item);
    });

    console.log('[INFO] inventory-gate ready');
  }

  if (document.readyState === 'complete') init();
  else window.addEventListener('load', init);

  /* ---------- COUNTRY WATCHER --------------------------------------- */
  setInterval(async () => {
    const cur = detectCountry();
    if (cur === visitorCountry) return;

    console.log('[INFO] country changed', visitorCountry, '=>', cur);
    visitorCountry = cur;

    await seedSharedFromCart();

    const vId = getCurrentVariantId();
    if (vId) {
      await fetchStock([vId]);
      updateVariantSwatches();
      updateATCButtons(vId);
      updatePdpQuantityControls(vId);
    }

    const bundleIds = collectBundleVariantIds();
    if (bundleIds.length) {
      await fetchStock(bundleIds);
      updateBundleUI();
    }

    const drawerIds = collectDrawerVariantIds();
    if (drawerIds.length) {
      await fetchStock(drawerIds);
      refreshDrawerLimits();
    }
  }, 10000);

})();
